/**
 * API hooks for Strategies
 */

import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useApiQuery,
  useApiPostMutation,
  useApiPatchMutation,
  useApiDeleteMutation,
} from "./useApiQuery.ts";
import type { ApiRequestConfig } from "../clients/awsApiClient.ts";
import { useIndicatorKey } from "./useIndicatorKey.ts";
import type { Period } from "@repo/core";
import AwsApiClient from "../clients/awsApiClient.ts";

// Strategy interface based on the provided example
export type PortfolioConstructionType = "LongOnly" | "ShortOnly" | "LongShort";

export type BacktestType = {
  ascending: boolean;
  backtestPeriod: string;
  leverage: number;
  longsShare: number;
  nSimulations: number;
  numberOfStocks: number;
  portfolioConstruction: PortfolioConstructionType;
  rollingWindow: number;
  shortsShare: number;
  simulRandomBacktest: boolean;
  startCash: number;
  period: Period;
  realTimeMonitoringStart?: string;
  realTimeMonitoringEnd?: string;
  backtestPeriodEnd?: string;
  backtestInProgress?: boolean;
};
export interface BasePortfolioType {
  userId: string;
  modelId: string;
  algorithm: string;
  factor: Record<string, number>;
  style: string;
  name: string;
  note?: string;
  preprocessing: boolean;
  strategyId: string;
  poolName: string;
  timeRange: string;
  Sharpe: number;
  Volatility: number;
  CAGR: number;
  meanTurnover: number;
  backtest: BacktestType;
}

export type PortfolioType = BasePortfolioType & {
  draft?: BasePortfolioType;
};

export interface Strategy {
  userId: string;
  modelId: string;
  algorithm: string;
  backtest: {
    ascending: boolean;
    backtestPeriod: string;
    leverage: number;
    longsShare: number;
    nSimulations: number;
    numberOfStocks: number;
    portfolioConstruction: PortfolioConstructionType;
    rollingWindow: number;
    shortsShare: number;
    simulRandomBacktest: boolean;
    startCash: number;
    period: Period;
    realTimeMonitoringStart?: string;
    realTimeMonitoringEnd?: string;
    backtestPeriodEnd?: string;
    backtestInProgress?: boolean;
  };
  factor: Record<string, number>;
  style: string;
  name: string;
  note?: string;
  preprocessing: boolean;
  strategyId: string;
  poolName: string;
  timeRange: string;
  Sharpe: number;
  Volatility: number;
  meanTurnover: number;
  CAGR: number;
}

interface CreateStrategyRequest {
  userId: string;
  modelId: string;
  algorithm: string;
  backtest: {
    ascending?: boolean;
    backtestPeriod: string;
    leverage?: number;
    longsShare?: number;
    nSimulations?: number;
    numberOfStocks?: number;
    portfolioConstruction?: "LongOnly" | "ShortOnly" | "LongShort";
    rollingWindow?: number;
    shortsShare?: number;
    simulRandomBacktest?: boolean;
    startCash?: number;
  };
  factor: Record<string, number>;
  name: string;
  preprocessing?: boolean;
  strategyId: string;
}

export interface CloneStrategyRequest {
  modelId: string;
  universe?: string;
  period?: string;
  newModelId?: string;
  deleteSource?: boolean;
  overwriteExisting?: boolean;
  backtest?: RecalculateStrategy;
}

export interface CloneResponse {
  modelId: string;
}

export interface RecalculateStrategy {
  startCash: number;
  period: string;
  numberOfStocks: number;
  portfolioConstruction: "LongOnly" | "ShortOnly" | "LongShort";
  backtestPeriod: string;
  leverage: number;
  longsShare: number;
  shortsShare: number;
  ascending: boolean;
}

interface UpdateStrategyRequest {
  backtest?: Partial<Strategy["backtest"]>;
  name?: string;
  note?: string;
}

interface Returns {
  LongLeg: number;
  LongOnly: number;
  LongPortfolioShares: Record<string, number>;
  LongPortfolioUSD: Record<string, number>;
  LongShort: number;
  LongTheShort: number;
  Longs: Record<string, number>;
  PickedLongs: string;
  PickedShorts: string;
  RebalanceDay: string;
  Scores: Record<string, number>;
  ShortLeg: number;
  ShortOnly: number;
  ShortPortfolioShares: Record<string, number>;
  ShortPortfolioUSD: Record<string, number>;
  Shorts: Record<string, number>;
  Strategy: number;
  Universe: number;
}

export interface ReturnsSet {
  [date: string]: Returns;
}

// Performance chart data types
export interface PerformanceMetrics {
  [date: string]: number;
}

export interface MimickingChartData {
  [indicatorName: string]: PerformanceMetrics;
}

export interface PerformanceChartData {
  [metricName: string]: PerformanceMetrics;
}

export interface TableRow {
  "Alpha-SP100": number;
  "Alpha-SP500": number;
  "Alpha-Universe": number;
  "Average-Drawdown": number;
  "Beta-SP100": number;
  "Beta-SP500": number;
  "Beta-Universe": number;
  CAGR: number;
  CVaR95: number;
  Calmar: number;
  "Drawdown-USD": number;
  "Final-Equity": number;
  "First-Day": string;
  Kurtosis: number;
  "Last-Day": string;
  "Longest-Drawdown": number;
  "Max-Drawdown": number;
  RoMaD: number;
  Sharpe: number;
  Skewness: number;
  Sortino: number;
  Sterling: number;
  VaR95: number;
  Volatility: number;
  "Win-Rate": number;
}

export interface TableColumns {
  LongOnly: TableRow;
  ShortOnly: TableRow;
  LongShort: TableRow;
  LongTheShort: TableRow;
  SP100: TableRow;
  SP500: TableRow;
  Universe: TableRow;
  Strategy: TableRow;
  [key: string]: TableRow; // Support dynamic universe columns like SP500_TECHNOLOGY, etc.
}

export interface DrawdownEntry {
  [date: string]: number;
}

export interface DrawdownData {
  LongOnly: DrawdownEntry;
  LongShort: DrawdownEntry;
  LongTheShort: DrawdownEntry;
  SP100: DrawdownEntry;
  SP500: DrawdownEntry;
  ShortOnly: DrawdownEntry;
  Strategy: DrawdownEntry;
  Universe: DrawdownEntry;
}

export interface AggregatedReturnData {
  Strategy: {
    [date: string]: number;
  };
}

export interface CorrelationMatrixType {
  [strategyId: string]: {
    [strategyId: string]: number;
  };
}

/**
 * Get all strategies
 */
export function useStrategies(config?: ApiRequestConfig) {
  return useApiQuery<Strategy[]>({
    path: "/strategy",
    config,
    queryKey: ["model"],
  });
}

/**
 * Get a specific strategy by ID
 */
export function useStrategyByKey(key: string, config?: ApiRequestConfig) {
  return useApiQuery<Strategy[]>({
    path: `/strategy/${key}`,
    config,
    queryKey: ["model", key],
    enabled: !!key,
  });
}

export function useStrategy(config?: ApiRequestConfig) {
  const { key } = useIndicatorKey();
  return useStrategyByKey(key, config);
}

export function useStrategyWithDraft(config?: ApiRequestConfig) {
  const { key } = useIndicatorKey();
  const rootStrategy = useStrategyByKey(key, config);
  const draftStrategy = useStrategyByKey(
    key !== "draft" ? `${key}_draft` : "",
    config
  );

  const completeStrategy = useMemo(() => {
    if (
      rootStrategy.data &&
      (draftStrategy.data || !draftStrategy.isFetching) &&
      rootStrategy.data.length > 0
    ) {
      return {
        ...rootStrategy.data[0],
        draft: draftStrategy.data ? draftStrategy.data[0] : undefined,
      } as PortfolioType;
    } else {
      return undefined;
    }
  }, [rootStrategy.data, draftStrategy.data, draftStrategy.isFetching]);

  return {
    data: completeStrategy,
    isLoading: rootStrategy.isLoading || draftStrategy.isLoading,
    isError: rootStrategy.isError,
    error: rootStrategy.error || draftStrategy.error,
    refetch: () => {
      rootStrategy.refetch();
      draftStrategy.refetch();
    },
  };
}

export function useCorelationMatrix(config?: ApiRequestConfig) {
  return useApiQuery<CorrelationMatrixType>({
    path: `/strategy/compounded-portfolio/correlation`,
    config,
    queryKey: ["compounded-portfolio"],
  });
}

export function useMimickingChartData(
  universe: string,
  period: string,
  indicators: string[],
  config?: ApiRequestConfig
) {
  return useApiQuery<MimickingChartData>({
    path: `/strategy/${universe}_${period}/indicator-charts?indicators=${indicators.join(
      ","
    )}`,
    config,
    queryKey: ["model", "chart", universe, period, indicators.join(",")],
    enabled: !!universe && !!period && indicators.length > 0,
  });
}

// Unlike per-indicator model data, the universe file is keyed by time range
// (ALL/5Y/3Y/1Y/YTD), each holding its own full date->value series.
export interface UniverseChartData {
  [universeId: string]: {
    [period: string]: PerformanceMetrics;
  };
}

/**
 * Get the universe-level equity curve (e.g. "admin_sp100_M") - loaded once
 * up front, before the user has picked any specific indicators to compare.
 * Returns all time ranges; pick the one matching the selected period.
 */
export function useUniverseChartData(
  universeId: string,
  config?: ApiRequestConfig
) {
  return useApiQuery<UniverseChartData>({
    path: `/strategy/${universeId}/indicator-charts?universe=${universeId}`,
    config,
    queryKey: ["model", "chart", "universe", universeId],
    enabled: !!universeId,
  });
}

export function usePredeliveredChartData(config?: ApiRequestConfig) {
  const { key } = useIndicatorKey();
  return useApiQuery<PerformanceChartData>({
    path: `/strategy/${key}/chart`,
    config,
    queryKey: ["model", "chart", key],
    enabled: !!key,
  });
}

export function usePredeliveredTableData(config?: ApiRequestConfig) {
  const { key } = useIndicatorKey();
  return useApiQuery<TableColumns>({
    path: `/strategy/${key}/table`,
    config,
    queryKey: ["model", "table", key],
    enabled: !!key,
  });
}

export function usePerformanceChartData(
  strategyId?: string,
  config?: ApiRequestConfig
) {
  return useApiQuery<PerformanceChartData>({
    path: `/strategy/${strategyId}/chart`,
    config,
    queryKey: ["model", "chart", strategyId],
    enabled: !!strategyId,
  });
}

export type EquityIntervalKey = "1M" | "3M" | "YTD" | "ALL";

export interface IntervalEquitySeries {
  [seriesName: string]: PerformanceMetrics;
}

export interface EquityInterval {
  start: string;
  end: string;
  partial: boolean;
  n_points: number;
  equity: IntervalEquitySeries;
}

export interface IntervalEquityData {
  as_of: string;
  start_cash: number;
  n_observations: number;
  success: boolean;
  intervals: Record<EquityIntervalKey, EquityInterval>;
  metrics: TableColumns;
}

/**
 * Get the multi-interval equity curve (1M/3M/YTD/ALL) for a strategy in a
 * single request. Each interval already contains its own full date->value
 * series per portfolio construction (Strategy/LongOnly/.../SP500/SP100), so
 * switching the visible period on the client is just picking a different
 * key — no refetch needed.
 */
export function useIntervalEquityData(
  strategyId?: string,
  config?: ApiRequestConfig
) {
  return useApiQuery<IntervalEquityData>({
    path: `/strategy/${strategyId}/interval-equity`,
    config,
    queryKey: ["model", "interval-equity", strategyId],
    enabled: !!strategyId,
  });
}

export function useMimickingData(
  universe: string,
  period: string,
  config?: ApiRequestConfig
) {
  return useApiQuery<Strategy[]>({
    path: `/strategy/${universe}_${period}`,
    config,
    queryKey: ["model", universe, period],
  });
}

export function useReturnData(config?: ApiRequestConfig) {
  const { key } = useIndicatorKey();
  return useApiQuery<ReturnsSet>({
    path: `/strategy/${key}/returns`,
    config,
    queryKey: ["model", "returns", key],
    enabled: !!key,
  });
}

export function useCurrentReturnData(config?: ApiRequestConfig) {
  const { key } = useIndicatorKey();
  return useApiQuery<ReturnsSet>({
    path: `/strategy/${key}/current-returns`,
    config,
    queryKey: ["model", "current-returns", key],
    enabled: !!key,
  });
}

/**
 * Custom hook that combines return data and current return data into a single aggregated object
 */
export function useAggregatedReturnData(config?: ApiRequestConfig) {
  const returnData = useReturnData(config);
  const currentReturnsData = useCurrentReturnData(config);

  // Combine the data using the same logic as in the component
  const aggregatedData = useMemo(() => {
    if (currentReturnsData.data && returnData.data) {
      return {
        ...currentReturnsData.data,
        ...returnData.data,
      };
    } else {
      return {};
    }
  }, [returnData.data, currentReturnsData.data]);

  return {
    data: aggregatedData,
    isLoading: returnData.isLoading || currentReturnsData.isLoading,
    isError: returnData.isError || currentReturnsData.isError,
    error: returnData.error || currentReturnsData.error,
    refetch: () => {
      returnData.refetch();
      currentReturnsData.refetch();
    },
  };
}

export function useDrawdownData(config?: ApiRequestConfig) {
  const { key } = useIndicatorKey();
  return useApiQuery<DrawdownData>({
    path: `/strategy/${key}/drawdown`,
    config,
    queryKey: ["model", "drawdown", key],
    enabled: !!key,
  });
}

export interface TurnoverStatistics {
  mean: number;
  median: number;
  std: number;
  min: number;
  max: number;
  count: number;
}

export interface TurnoverData {
  model_id: string;
  user_id: string;
  start_date: string;
  number_of_stocks: number;
  calculation_timestamp: string;
  files_processed: number;
  total_rebalance_dates: number;
  mean_turnover: number;
  turnover_series: {
    [date: string]: number;
  };
  turnover_statistics: TurnoverStatistics;
}

/**
 * Get turnover data for a strategy
 * Returns portfolio turnover metrics including time series and statistics
 */
export function useTurnoverData(config?: ApiRequestConfig) {
  const { key } = useIndicatorKey();
  return useApiQuery<TurnoverData>({
    path: `/strategy/${key}/turnover`,
    config,
    queryKey: ["model", "turnover", key],
    enabled: !!key,
  });
}

export function useAggregatedReturnsData(config?: ApiRequestConfig) {
  const { key } = useIndicatorKey();
  return useApiQuery<DrawdownData>({
    path: `/strategy/${key}/aggregated-returns`,
    config,
    queryKey: ["model", "aggregated-returns", key],
    enabled: !!key,
  });
}

export async function fetchScoreData(
  modelId: string,
  date: string,
  config?: ApiRequestConfig
) {
  return AwsApiClient.apiGet(`/strategy/${modelId}/score/${date}`, config);
}

/**
 * Create a new strategy
 */
export function useCreateStrategy() {
  return useApiPostMutation<Strategy, CreateStrategyRequest>({
    getPath: () => "/strategies",
    getConfig: (variables) => ({
      body: variables,
    }),
  });
}

export function useCloneStrategy() {
  return useApiPostMutation<CloneResponse, CloneStrategyRequest>({
    getPath: () => "/strategy/clone",
    getConfig: (variables) => ({
      body: variables,
    }),
  });
}

export function useRecalculateStrategy(id: string) {
  return useApiPostMutation<void, RecalculateStrategy>({
    getPath: () => `/strategy/${id}/recalculate`,
    getConfig: (variables) => ({
      body: variables,
    }),
  });
}

/**
 * Update an existing strategy
 */
export function useUpdateStrategy() {
  const queryClient = useQueryClient();

  return useApiPatchMutation<
    Strategy,
    UpdateStrategyRequest & { modelId: string }
  >({
    getPath: (variables) => `/strategy/${variables.modelId}`,
    getConfig: (variables) => {
      const { modelId, ...updateData } = variables;
      console.log("Updating strategy with data:", modelId);
      return {
        body: updateData,
      };
    },
    onSuccess: (_data, variables) => {
      // Invalidate strategy-specific queries
      queryClient.invalidateQueries({ queryKey: ["model"] });
      // Also invalidate specific strategy query if we know the key
      if (variables.modelId) {
        queryClient.invalidateQueries({
          queryKey: ["model", variables.modelId],
        });
      }
    },
  });
}

/**
 * Delete a strategy
 */
export function useDeleteStrategy(modelId: string) {
  const queryClient = useQueryClient();

  return useApiDeleteMutation<void, { modelId: string }>({
    getPath: (variables) => `/strategy/${variables.modelId}`,
    onSuccess: () => {
      queryClient.setQueryData(["model"], (oldData: Strategy[]) =>
        oldData.filter((model) => model.modelId !== modelId)
      );
      queryClient.invalidateQueries({ queryKey: ["model", modelId] });
    },
  });
}
